import { useCallback, useState } from "preact/hooks";

import {
  fetchCompletionRateReport,
  fetchHealthLogReport,
  fetchOverdueAgingReport,
  fetchWorkloadReport,
} from "../lib/api";
import type {
  CompletionRateResponse,
  HealthLogResponse,
  OverdueAgingResponse,
  WorkloadResponse,
} from "../types";

/**
 * Sprint-009 B4 — `/reports`'s four per-tab data hooks (design-phase-b.md
 * §5, ADR-002 Amendment 5 decision 6). All four endpoints are admin/
 * manager/api server-side; every hook here takes an `enabled` flag (the
 * SAME `canHandOutWork` gate `hooks/useProjects.ts::useProjects` already
 * establishes as a structural, fetch-level guard — a member session must
 * never issue any of these four requests, not just have the tab hidden).
 *
 * Three of the four (`completions`/`health-log`/`workload`) are explicit
 * `load(from, to[, animalId])` calls, not auto-fetch-on-mount — §5.2's
 * shared date-range toolbar is an "Apply" button, not live-refetch-on-
 * every-keystroke, so `ReportsScreen` calls `load` once with the default
 * "last 30 days" range on first activating a tab, then again only on
 * "Apply." `overdue` (no date-range semantic at all, §5.2/ADR-002 A5
 * decision 6 #2) is the one exception — it fetches on `enabled` alone,
 * matching a plain `useProjects`-shaped hook exactly.
 */
export function useCompletionRateReport(enabled: boolean) {
  const [data, setData] = useState<CompletionRateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (from: string, to: string) => {
      if (!enabled) return;
      setLoading(true);
      setError(null);
      try {
        setData(await fetchCompletionRateReport(from, to));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't load this report.");
      } finally {
        setLoading(false);
      }
    },
    [enabled],
  );

  return { data, loading, error, load };
}

export function useOverdueAgingReport(enabled: boolean) {
  const [data, setData] = useState<OverdueAgingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      setData(await fetchOverdueAgingReport());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load this report.");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  return { data, loading, error, load };
}

export function useHealthLogReport(enabled: boolean) {
  const [data, setData] = useState<HealthLogResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (from: string, to: string, animalId?: string | null) => {
      if (!enabled) return;
      setLoading(true);
      setError(null);
      try {
        setData(await fetchHealthLogReport(from, to, animalId));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't load this report.");
      } finally {
        setLoading(false);
      }
    },
    [enabled],
  );

  return { data, loading, error, load };
}

export function useWorkloadReport(enabled: boolean) {
  const [data, setData] = useState<WorkloadResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (from: string, to: string) => {
      if (!enabled) return;
      setLoading(true);
      setError(null);
      try {
        setData(await fetchWorkloadReport(from, to));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't load this report.");
      } finally {
        setLoading(false);
      }
    },
    [enabled],
  );

  return { data, loading, error, load };
}
